import {
  DOCUMENT,
} from '@angular/common';
import {
  Component,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import {
  GuardsCheckStart,
  NavigationCancel,
  NavigationEnd,
  NavigationSkipped,
  NavigationStart,
  RouteConfigLoadEnd,
  Router,
  Scroll,
} from '@angular/router';
import {
  ErrorMessage,
} from '@dapi/sdk';
import {
  createDESLoggerLogServerClient,
  DESLogServerLoggerClientOptions,
  LOG_SERVER_LOGGER_CLIENT_OPTIONS,
} from '@des/services/logging';
import {
  MessagesStore,
  resetMessages,
  selectAllErrorMessages,
} from '@des/store/messages';
import {
  OrderStore,
  selectCurrentOrder,
  selectCurrentOrderStatus,
} from '@des/store/order';
import {
  OrderEligibilitiesStore,
  selectOrderEligibilitiesStorePendingStatus,
} from '@des/store/order-eligibilities';
import {
  select,
  Store,
} from '@ngrx/store';
import {
  LangChangeEvent,
} from '@ngx-translate/core';
import {
  EventTrackState,
  selectEventTrackState,
} from '@o3r/analytics';
import {
  ApiFactoryService,
} from '@o3r/apis-manager';
import {
  PlaceholderRulesEngineActionHandler,
} from '@o3r/components/rules-engine';
import {
  ConfigurationBaseService,
  ConfigurationObserver,
  ConfigurationState,
  DynamicConfigurable,
} from '@o3r/configuration';
import {
  ConfigurationRulesEngineActionHandler,
} from '@o3r/configuration/rules-engine';
import {
  isProductionEnvironment,
} from '@o3r/core';
import {
  DynamicContentService,
} from '@o3r/dynamic-content';
import {
  AssetRulesEngineActionHandler,
} from '@o3r/dynamic-content/rules-engine';
import {
  CUSTOM_LOCALIZATION_CONFIGURATION_TOKEN,
  LocalizationConfiguration,
  LocalizationService,
  TextDirectionService,
} from '@o3r/localization';
import {
  LocalizationRulesEngineActionHandler,
} from '@o3r/localization/rules-engine';
import {
  LoggerService,
} from '@o3r/logger';
import {
  RulesEngineRunnerService,
  Ruleset,
  setRulesetsEntities,
} from '@o3r/rules-engine';
import {
  AnalyticsService,
  Flow,
} from '@refx/analytics-core';
import {
  AppBootstrapConfig,
  BOOTSTRAP_CONFIGURATION_TOKEN,
  DEFAULT_LOCALES,
  defaultLocalizationAppProp,
  FlowType,
  getMainLangFromLocale,
} from '@refx/shared-common';
import {
  ABTestingBridgeService,
  AnalyticsUtilityService,
  AppConfigService,
  AppInitializationService,
  DocumentTitleService,
  DynamicLocaleService,
  LazyLoaderDialogService,
  LoadingStateService,
  RouterLoadingStateService,
  SkeletalLoaderUtilityService,
  TagsService,
} from '@refx/shared-services';
import {
  AppConfigStore,
  selectAppConfigAnimationsDisabled,
  selectFlowType,
  UiStatusStore,
} from '@refx/shared-store';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
  Subscription,
} from 'rxjs';
import {
  filter,
  map,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import {
  getDigitalTouchpointData,
  ineligibleRoutesForSkeleton,
  skeletalRoutes,
} from '../configuration/index';
import {
  environment,
} from '../environments';
import {
  AppPreferencesFactsService,
  OrderFactsService,
  PortalFactsService,
  TechnicalDetailsFactsService,
  UiStatusFactsService,
} from '../facts/index';
import {
  PreviousUrlService,
} from '../services/index';
import {
  APP_CONFIG_DEFAULT,
  APP_CONFIG_ID,
  AppConfig,
} from './app.config';

@Component({
  selector: 'app',
  templateUrl: './app.template.html',
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy, DynamicConfigurable<AppConfig> {
  /**
   * Config attribute to be compliant with DynamicConfigurable interface. Not used at the moment, root component cannot have inputs.
   */
  public config: Partial<AppConfig> | undefined;

  /**
   * Configuration stream based on the stored application configuration
   */
  public config$: Observable<AppConfig>;

  /**
   * Application dynamic configuration based on the configuration service if used by the application
   */
  private readonly dynamicConfig$: ConfigurationObserver<AppConfig>;

  /**
   * List of subscriptions to unsuscribe on destroy
   */
  protected subscriptions: Subscription[] = [];

  /**
   * Boolean indicating if the animations should be disabled
   *
   * @see https://angular.io/api/animations/trigger#disabling-animations
   */
  public animationsDisabled$: Observable<boolean>;

  /**
   * Observable for isPending flag
   */
  public isPending$: Observable<boolean>;

  /**
   * Observable that emits when the application has completed loading based on the TTI (Time to Interact) computation
   */
  public loadingComplete$: Observable<boolean>;

  /**
   * Function to lazily load the debug panel
   */
  public displayDebugPanel = () => import(/* webpackChunkName: "dev-tools" */ '@refx/shared-components').then((module) => module.DevtoolsPanelComponentStandalone);

  public showDebugPanel = false;

  /**
   * Variable to store current url
   */
  private currentUrl: string;

  /**
   * Variable to store previous url
   */
  private previousUrl: string;

  /**
   * Boolean to track the initialization of the lazy devtools chunks
   */
  public firstRouteActivated = false;

  /**
   * Boolean that decides if the skeletal loader should be displayed
   */
  public displaySkeletalLoader = false;

  /**
   * A subject to trigger the monitoring of the router events pertaining to the loading states
   */
  protected monitorRouterStatus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(BOOTSTRAP_CONFIGURATION_TOKEN) private readonly bootstrapConfig: AppBootstrapConfig,
    @Inject(CUSTOM_LOCALIZATION_CONFIGURATION_TOKEN) public customLocalizationConfig: LocalizationConfiguration,
    @Inject(LOG_SERVER_LOGGER_CLIENT_OPTIONS) private readonly logServerLoggerClientOptions: DESLogServerLoggerClientOptions,
    private readonly router: Router,
    private readonly rulesEngine: RulesEngineRunnerService,
    private readonly placeholderRulesEngineActionHandler: PlaceholderRulesEngineActionHandler,
    private readonly localizationRulesEngineActionHandler: LocalizationRulesEngineActionHandler,
    private readonly assetRulesEngineActionHandler: AssetRulesEngineActionHandler,
    private readonly configurationRulesEngineActionHandler: ConfigurationRulesEngineActionHandler,
    private readonly localizationService: LocalizationService,
    private readonly textDirectionService: TextDirectionService,
    private readonly store: Store<AppConfigStore & ConfigurationState & OrderEligibilitiesStore & MessagesStore & UiStatusStore & OrderStore>,
    private readonly configService: ConfigurationBaseService,
    private readonly abTestingBridgeService: ABTestingBridgeService,
    private readonly analyticsService: AnalyticsService,
    private readonly injector: Injector,
    private readonly dynamicContentService: DynamicContentService,
    private readonly previousUrlService: PreviousUrlService,
    private readonly analyticsUtilityService: AnalyticsUtilityService,
    private readonly appInitializationService: AppInitializationService,
    private readonly appPreferences: AppPreferencesFactsService,
    private readonly portalFacts: PortalFactsService,
    private readonly orderFacts: OrderFactsService,
    private readonly technicalDetails: TechnicalDetailsFactsService,
    private readonly uiStatusFacts: UiStatusFactsService,
    public appConfigService: AppConfigService,
    private readonly apiFactoryService: ApiFactoryService,
    private readonly loggerService: LoggerService,
    private readonly loadingStateService: LoadingStateService,
    private readonly routerLoadingService: RouterLoadingStateService,
    private readonly lazyLoaderDialogService: LazyLoaderDialogService,
    private readonly skeletalLoaderUtilityService: SkeletalLoaderUtilityService,
    private readonly renderer: Renderer2
  ) {
    if (!environment.DEBUG_MODE && !!this.bootstrapConfig.loggingBaseUrl) {
      this.loggerService.registerClient(createDESLoggerLogServerClient(this.apiFactoryService, this.logServerLoggerClientOptions));
    }

    this.setBodyLanguageDirection();
    this.injector.get(DocumentTitleService);
    this.injector.get(TagsService);
    this.injector.get(DynamicLocaleService);

    this.appInitializationService.initialize();
    this.dynamicConfig$ = new ConfigurationObserver<AppConfig>(APP_CONFIG_ID, APP_CONFIG_DEFAULT, this.configService);
    this.config$ = this.dynamicConfig$.asObservable();
    this.document = document;

    this.isSkeletalLoaderEnabled();

    if (this.bootstrapConfig.downloadFallbackLanguageChunk) {
      this.localizationService.getTranslateService().setDefaultLang(defaultLocalizationAppProp.fallbackLanguage || DEFAULT_LOCALES.ENGB);
    }
    this.localizationService.configure();
    this.animationsDisabled$ = store.pipe(select(selectAppConfigAnimationsDisabled));

    this.subscriptions.push(
      this.store.pipe(select(selectFlowType)).subscribe((flowType) => {
        let flow: Flow;
        switch (flowType) {
          case FlowType.refund: {
            flow = Flow.REFUND;
            break;
          }
          default: {
            flow = Flow.SELF_REACCOMMODATION;
          }
        }
        this.setupAnalytics(flow);
      }),
      // If time out, display Response timeout popup
      this.store.pipe(
        select(selectAllErrorMessages),
        filter((messages) => messages.some((errorMessage: ErrorMessage) => errorMessage.title.includes('Response timeout')))
      ).subscribe(() => {
        // Need the first value of the observable to emit in order for the dialog to open
        firstValueFrom(
          this.lazyLoaderDialogService.open(
            () => import('@refx/shared-components').then((module) => module.TimeOutDialogComponent),
            {
              data: {
                config: {},
                inputs: {},
                outputs: {}
              }
            }
          )
        );
        // clear the messages in the store after opening the timeout dialog
        this.store.dispatch(resetMessages());
      })
    );

    this.isPending$ = combineLatest([
      this.store.pipe(select(selectOrderEligibilitiesStorePendingStatus)),
      this.store.pipe(
        select(selectCurrentOrderStatus),
        map((status) => !!status && status.isPending)
      ),
      this.loadingStateService.shouldDisplayLoader$
    ]).pipe(map((arePending: (boolean | undefined)[]) => arePending.some(Boolean)));

    this.loadingComplete$ = this.store.pipe(
      select(selectEventTrackState),
      map((state: EventTrackState) => !!state && state.isTTIComputed)
    );
  }

  /**
   * Set the language direction on the body element.
   * It is required to align the skeleton loader with the language direction correctly.
   */
  private setBodyLanguageDirection() {
    const language = this.localizationService.getCurrentLanguage();
    const direction = this.customLocalizationConfig.rtlLanguages.includes(language.split('-')[0]) ? 'rtl' : 'ltr';
    this.renderer.setAttribute(this.document.body, 'dir', direction);
  }

  /**
   * Sets up analytics
   *
   * @param flow flow type
   */
  private setupAnalytics(flow: Flow) {
    this.analyticsService.setCurrentFlow(flow);
    this.analyticsService.configureDataLake('RefX', 'RefX', {
      digitalTouchpointDetails: {
        flow: flow,
        name: 'RefX_offtheshelf',
        clientPhase: isProductionEnvironment(this.document.body.dataset) ? 'PRD' : this.bootstrapConfig.environment,
        language: getMainLangFromLocale(this.localizationService.getCurrentLanguage()).toUpperCase()
      }
    });
    this.analyticsService.setupIsProd(isProductionEnvironment(document.body.dataset));
    this.store.pipe(
      select(selectCurrentOrder),
      filter((order) => !!order),
      map((order) => order?.servicingPointOfSale?.pointOfSaleId || order?.creationPointOfSale?.pointOfSaleId || ''),
      take(1)
    ).subscribe((officeIdFromOrder) => {
      const digitalTouchpointData = getDigitalTouchpointData();
      const portalFacts = this.appInitializationService.getPortalFacts();
      const countryCodeFromPortalFacts = (portalFacts || []).find((portalFact) => portalFact.key === 'countryCode');
      this.analyticsService.setupDigitalTouchPoint(
        getMainLangFromLocale(this.localizationService.getCurrentLanguage()),
        digitalTouchpointData.digitalTouchPoint?.touchpointInfo.market || countryCodeFromPortalFacts?.value || this.appInitializationService.getPointOfSale() || '',
        digitalTouchpointData.digitalTouchPoint?.category?.primaryCategory,
        digitalTouchpointData.digitalTouchPoint?.touchpointInfo.officeID || officeIdFromOrder,
        digitalTouchpointData.digitalTouchPoint?.touchpointInfo.sessionID,
        digitalTouchpointData.digitalTouchPoint?.category?.subCategory || Flow.SERVICING,
        digitalTouchpointData.digitalTouchPoint?.touchpointInfo.site,
        digitalTouchpointData.digitalTouchPoint?.touchpointInfo.airlineCode,
        digitalTouchpointData.digitalTouchPoint?.touchpointInfo.bookingFlow,
        digitalTouchpointData.digitalTouchPoint?.touchpointInfo.currency
      );
    });
  }

  /**
   * Set the language in the DOM on language change
   * until https://github.com/ngx-translate/core/issues/565 is implemented
   */
  private setDOMLanguage() {
    this.subscriptions.push(
      this.localizationService.getTranslateService().onLangChange.subscribe((params: LangChangeEvent) => {
        if (this.document.documentElement) {
          this.document.documentElement.lang = params.lang;
        }
      })
    );
  }

  private setupRulesets() {
    this.rulesEngine.actionHandlers.add(this.placeholderRulesEngineActionHandler);
    this.rulesEngine.actionHandlers.add(this.localizationRulesEngineActionHandler);
    this.rulesEngine.actionHandlers.add(this.assetRulesEngineActionHandler);
    this.rulesEngine.actionHandlers.add(this.configurationRulesEngineActionHandler);
    this.appPreferences.register();
    this.portalFacts.register();
    this.orderFacts.register();
    this.technicalDetails.register();
    this.uiStatusFacts.register();
    this.abTestingBridgeService.register();

    const rulesetsFilePaths = [];
    if (this.bootstrapConfig.useGeneratedRulesets) {
      rulesetsFilePaths.push('rules/rulesets.json');
    }
    if (this.bootstrapConfig.useAssetsRulesets) {
      rulesetsFilePaths.push('assets/rules/rulesets.json');
    }

    if (rulesetsFilePaths.length > 0) {
      const rulesetsFetch$: Observable<Promise<Ruleset[]>>[] = rulesetsFilePaths.map(
        (rulesetsFilePath) => this.dynamicContentService.getContentPathStream(rulesetsFilePath).pipe(
          map(async (path: string) => {
            const resultCall = await fetch(path);
            return resultCall.ok ? (await resultCall.json()).rulesets as Ruleset[] ?? [] : [];
          })
        )
      );

      this.subscriptions.push(
        combineLatest(rulesetsFetch$).subscribe(
          async (rulesetsPromises: Promise<Ruleset[]>[]) => {
            const fetchedRulesets = await Promise.all(rulesetsPromises);
            this.store.dispatch(setRulesetsEntities({
              entities: Array.prototype.concat(...fetchedRulesets)
            }));
          }
        )
      );
    }
  }

  /**
   * Monitor the loading states of the sources required during the bootstrap of the application
   *
   * @param shouldMonitorRouterStatus boolean to indicate if the router events should be monitored
   */
  public monitorLoadingStates(shouldMonitorRouterStatus = true) {
    if (shouldMonitorRouterStatus) {
      this.routerLoadingService.monitorRouterEventsStatus(
        (event: any) => !(
          event instanceof Scroll
          || event instanceof NavigationCancel
          || event instanceof RouteConfigLoadEnd
          || event instanceof GuardsCheckStart
          || event instanceof NavigationSkipped
        )
      );
    }
  }

  /**
   * Checks if the skeletal loader should be displayed
   */
  public async isSkeletalLoaderEnabled() {
    try {
      const isEnabled$: Observable<boolean> = this.skeletalLoaderUtilityService.shouldDisplayForRoute$(skeletalRoutes, ineligibleRoutesForSkeleton);
      this.displaySkeletalLoader = await firstValueFrom(isEnabled$);
    } catch {} finally {
      this.monitorLoadingStates(!this.displaySkeletalLoader);
    }
  }

  public ngOnInit() {
    this.setDOMLanguage();

    this.subscriptions.push(this.textDirectionService.onLangChangeSubscription());
    this.subscriptions.push(
      this.router.events.pipe(
        withLatestFrom(this.monitorRouterStatus$.asObservable()),
        filter(([event, shouldMonitorRouterStatus]) => shouldMonitorRouterStatus && event instanceof NavigationEnd)
      ).subscribe(() => {
        this.monitorLoadingStates();
        this.monitorRouterStatus$.next(false);
      }),
      this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe((event: NavigationStart) => {
        // Set the T0 for TTI computation when navigation starts
        this.analyticsUtilityService.setPageStartTime();
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
        this.previousUrlService.setPreviousUrl(this.previousUrl);
      })
    );

    this.setupRulesets();
  }

  /**
   * Triggered when a route is activated. This callback is used
   * for downloading all the lazy chunks like the devtools modules
   * and the lazy components rendered throught the lazy outlet
   */
  public onRouteActivated() {
    if (!this.firstRouteActivated) {
      this.firstRouteActivated = true;
      this.monitorRouterStatus$.next(true);
      this.showDebugPanel = this.appInitializationService.isTraceEnabled();
      this.appInitializationService.initializeDevTools();
    }
  }

  public ngOnDestroy() {
    // clean the subscriptions
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
